// hooks/useSpotPositions.ts
import { useMemo } from "react";

export interface BinanceTrade {
    symbol: string;
    isBuyer: boolean;      // true = BUY, false = SELL
    price: string;         // "136.5000"
    qty: string;           // "0.500"
    commission?: string;
    commissionAsset?: string;
    time?: number;
}

export type SpotPosition = {
    symbol: string;
    qty: number;
    avgEntry: number | null;
    currentPrice: number | null;
    unrealizedPnl: number;
    roe: number;
    realizedPnl: number;
};

/**
 * trades  — сырые трейды Spot (Binance myTrades)
 * prices  — текущие цены, например из BinanceContext.prices: { SOLUSDT: "136.12", ... }
 */
export function useSpotPositions(
    trades: BinanceTrade[] | null | undefined,
    prices: Record<string, string | number>
): SpotPosition[] {

    return useMemo(() => {
        if (!trades || trades.length === 0) return [];

        // Группируем трейды по символам
        const bySymbol: Record<string, BinanceTrade[]> = {};

        for (const t of trades) {
            if (!bySymbol[t.symbol]) {
                bySymbol[t.symbol] = [];
            }
            bySymbol[t.symbol].push(t);
        }

        const result: SpotPosition[] = [];

        for (const symbol of Object.keys(bySymbol)) {
            const symbolTrades = bySymbol[symbol].slice();

            // Сортируем по времени, если есть поле time
            symbolTrades.sort((a, b) => {
                const ta = a.time ?? 0;
                const tb = b.time ?? 0;
                return ta - tb;
            });

            // BUY-лоты, которые остаются после частичного/полного закрытия SELL’ами
            type Lot = { qty: number; price: number };

            const buyLots: Lot[] = [];
            let realizedPnl = 0;

            for (const t of symbolTrades) {
                const price = parseFloat(t.price);
                const qty = parseFloat(t.qty);

                if (qty <= 0) continue;

                if (t.isBuyer) {
                    // Покупка → добавляем лот
                    buyLots.push({ qty, price });
                } else {
                    // Продажа → закрываем ранее купленные лоты (FIFO)
                    let remain = qty;

                    while (remain > 0 && buyLots.length > 0) {
                        const lot = buyLots[0];
                        const matched = Math.min(remain, lot.qty);

                        // Реализованный PnL для закрытой части
                        realizedPnl += (price - lot.price) * matched;

                        lot.qty -= matched;
                        remain -= matched;

                        if (lot.qty <= 0) {
                            buyLots.shift();
                        }
                    }

                    // Если remain > 0 и buyLots уже пуст — на споте формально не должно быть,
                    // но в таком случае считаем, что позиция уже нулевая.
                }
            }

            // Открытая позиция = всё, что осталось в buyLots
            const openQty = buyLots.reduce((sum, lot) => sum + lot.qty, 0);
            const totalCost = buyLots.reduce((sum, lot) => sum + lot.qty * lot.price, 0);

            let avgEntry: number | null = null;
            if (openQty > 0 && totalCost > 0) {
                avgEntry = totalCost / openQty;
            }

            const rawPrice = prices[symbol];
            const currentPrice =
                rawPrice !== undefined && rawPrice !== null
                    ? Number(rawPrice)
                    : null;

            let unrealizedPnl = 0;
            let roe = 0;

            if (openQty > 0 && avgEntry !== null && currentPrice !== null) {
                unrealizedPnl = (currentPrice - avgEntry) * openQty;
                const invested = avgEntry * openQty;
                if (invested > 0) {
                    roe = (unrealizedPnl / invested) * 100;
                }
            }

            // Если qty <= 0 — позиция полностью закрыта, можно не пушить
            if (openQty <= 0 && realizedPnl === 0) {
                continue;
            }

            result.push({
                symbol,
                qty: openQty,
                avgEntry,
                currentPrice,
                unrealizedPnl,
                roe,
                realizedPnl,
            });
        }

        return result;
    }, [trades, prices]);
}